import React from 'react';
import {  Input, Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import CriteriaGroup from './CriteriaGroup';

const Criteria = ({
  title,
  required = false,
  criteriaGroups,
  setCriteriaGroups,
  generateSQL,
  setGeneratedSQL,
  columnNames = [],
  operators = [],
  metadata = {},
  onColumnChange,
  isColumnLoading = false,
  isMetaDataLoading = false,
  columnTypes = {},
  isSelectionCriteria = true,
  initialSQL = ''
}) => {
  const updateNestedGroups = (groups, targetId, updateFn) => {
    return groups.map(group => {
      if (group.id === targetId) {
        return updateFn(group);
      }
      if (group.criteria) {
        return {
          ...group,
          criteria: updateNestedGroups(group.criteria, targetId, updateFn)
        };
      }
      return group;
    });
  };

  const deleteNestedGroup = (groups, targetId) => {
    return groups.filter(group => {
      if (group.id === targetId) {
        return false;
      }
      if (group.criteria) {
        group.criteria = deleteNestedGroup(group.criteria, targetId);
      }
      return true;
    });
  };

  const handleUpdate = (groupId, field, value) => {
    setCriteriaGroups(prevGroups => {
      const newGroups = updateNestedGroups(prevGroups, groupId, (group) => ({
        ...group,
        [field]: value
      }));
      const sql = generateSQL(newGroups);
      setGeneratedSQL(sql);
      return newGroups;
    });
  };

  const handleDelete = (groupId) => {
    if (criteriaGroups.some(g => g.id === groupId)) {
      setCriteriaGroups(prevGroups => {
        const newGroups = prevGroups.filter(g => g.id !== groupId);
        const sql = generateSQL(newGroups);
        setGeneratedSQL(sql);
        return newGroups;
      });
    } else {
      setCriteriaGroups(prevGroups => {
        const newGroups = deleteNestedGroup(prevGroups, groupId);
        const sql = generateSQL(newGroups);
        setGeneratedSQL(sql);
        return newGroups;
      });
    }
  };

  const handleAddCriteria = (groupId) => {
    setCriteriaGroups(prevGroups => {
      const newGroups = updateNestedGroups(prevGroups, groupId, (group) => ({
        ...group,
        criteria: [...group.criteria, {
          id: uuidv4(),
          type: 'rule',
          columnName: undefined,
          operator: undefined,
          values: []
        }]
      }));
      
      const sql = generateSQL(newGroups);
      setGeneratedSQL(sql);
      return newGroups;
    });
  };

  const handleAddGroup = (groupId) => {
    setCriteriaGroups(prevGroups => {
      const newGroups = updateNestedGroups(prevGroups, groupId, (group) => ({
        ...group,
        criteria: [...group.criteria, {
          id: uuidv4(),
          type: 'group',
          logicType: 'And',
          isNot: false,
          criteria: [{
            id: uuidv4(),
            type: 'rule',
            columnName: undefined,
            operator: undefined,
            values: []
          }]
        }]
      }));
      
      const sql = generateSQL(newGroups);
      setGeneratedSQL(sql);
      return newGroups;
    });
  };

  const handleCriteriaUpdate = (groupId, criteriaId, field, value) => {
    setCriteriaGroups(prevGroups => {
      const newGroups = updateNestedGroups(prevGroups, groupId, (group) => ({
        ...group,
        criteria: group.criteria.map(criteria => {
          if (criteria.id === criteriaId) {
            return { ...criteria, [field]: field === 'values' ? (Array.isArray(value) ? value : [value]) : value };
          }
          return criteria;
        })
      }));
      
      const sql = generateSQL(newGroups);
      setGeneratedSQL(sql);
      return newGroups;
    });
  };

  const handleDeleteCriteria = (groupId, criteriaId) => {
    setCriteriaGroups(prevGroups => {
      const newGroups = updateNestedGroups(prevGroups, groupId, (group) => ({
        ...group,
        criteria: group.criteria.filter(criteria => criteria.id !== criteriaId)
      }));
      
      const sql = generateSQL(newGroups);
      setGeneratedSQL(sql);
      return newGroups;
    });
  };

  return (
    <div className="criteria-container">
      <label>
        {required && <span style={{ color: '#FF4E50', fontSize: "12px" }}>* </span>}
        {title}
      </label>
      
      {criteriaGroups.map(group => (
        <CriteriaGroup
          key={group.id}
          group={group}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onAddCriteria={handleAddCriteria}
          onAddGroup={handleAddGroup}
          onCriteriaUpdate={handleCriteriaUpdate}
          onDeleteCriteria={handleDeleteCriteria}
          level={0}
          parentLogicType={null}
          isSelectionCriteria={isSelectionCriteria}
          columnNames={columnNames}
          operators={operators}
          metadata={metadata}
          onColumnChange={onColumnChange}
          isColumnLoading={isColumnLoading}
          isMetaDataLoading={isMetaDataLoading}
          columnTypes={columnTypes}
          initialSQL={initialSQL}
        />
      ))}
      
      <Form.Item>
        <Input.TextArea
          value={generateSQL(criteriaGroups)}
          placeholder={`${title}`}
          readOnly
          style={{
            backgroundColor: '#f5f5f5',
            fontFamily: 'monospace'
          }}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      </Form.Item>
    </div>
  );
};

export default Criteria; 